import { createElement } from "preact";
import { internal } from "../../internal";
import { assert, assertEquals } from "../../utils";
import { BlackboardType } from "../behavior-tree";
import { ContainerActionBehavior, DefaultActionContainerType } from "./action";
import { ActionQr } from "../../components/action-qr";
import { InternalBehaviorTreeUpdateEvent } from "../../private-event-types";
import { hasCustomQrArt } from "../../components/action-qr-custom-art";

export class ActionQrBehavior extends ContainerActionBehavior {
  constructor(
    protected bb: BlackboardType,
    private actionIndex: string,
  ) {
    super(bb);
  }

  enter() {
    const qrAction =
      this.bb.world?.paymentEntity?.entity.actions[Number(this.actionIndex)];

    assertEquals(qrAction?.type, "PRESENT_TO_CUSTOMER");
    assert(this.bb.world);
    assert(this.bb.channel);
    assert(this.bb.world.paymentEntity);

    let channelCodeForQrArt = this.bb.channel.channel_code;

    if (
      this.bb.mock &&
      typeof this.bb.channelProperties?.mock_channel_code_for_qr_art ===
        "string"
    ) {
      // in mock mode, override the channel for custom art purposes with the user selected value
      channelCodeForQrArt =
        this.bb.channelProperties.mock_channel_code_for_qr_art;
    }

    const qrHasCustomArt = hasCustomQrArt(channelCodeForQrArt);

    const container = this.bb.sdk[internal].liveComponents.actionContainer;

    const actionQrProps: Parameters<typeof ActionQr>[0] = {
      amount: this.bb.world.session.amount,
      businessName: this.bb.world.business.name ?? "",
      channelCodeForQrArt: channelCodeForQrArt,
      channelName: this.bb.channel.brand_name,
      channelLogo: this.bb.channel.brand_logo_url,
      currency: this.bb.world.session.currency,
      hideUi: container?.getAttribute("data-qr-code-only") === "true" || false,
      onAffirm: this.affirmPayment.bind(this),
      qrString: qrAction.value,
      title: qrAction.action_subtitle,
      t: this.bb.sdk.t.bind(this.bb.sdk),
    };

    const defaultActionContainerType = qrHasCustomArt
      ? DefaultActionContainerType.QrWithCustomArt
      : DefaultActionContainerType.Generic;
    this.cleanupFn = this.ensureHasActionContainer(defaultActionContainerType);
    this.populateActionContainer(() => createElement(ActionQr, actionQrProps));
  }

  /**
   * Fired when user affirms they have made the payment by clicking
   * the affirm button.
   */
  affirmPayment() {
    if (this.bb.sdk.isProdLive()) {
      // live mode
      this.bb.pollImmediatelyRequested = true;
    } else {
      this.bb.simulatePaymentRequested = true;
    }
    this.bb.dispatchEvent(new InternalBehaviorTreeUpdateEvent());
  }

  exit() {
    super.exit();
  }
}
